'use strict'

import {
  KMZ_DEFAULT_ACTION_GROUP_MODE,
  KMZ_DEFAULT_ACTION_TRIGGER_TYPE,
  normalizeKmzActionEntry,
  applyKmzActionFileSuffixDisplayMode,
  resolveKmzActionFileSuffixPointNameTokens,
  normalizeKmzIntegerLikeText,
  normalizeKmzActionTriggerEntry,
  normalizeKmzActionGroupEntry
} from './portal-kmz-action-utils.js'
import {
  normalizeKmlActionEntry
} from './portal-editors-kml.js'
import {
  normalizeKmzActionGroupsForPoint,
  normalizeKmzActionParamsForActuator
} from './portal-editors-kmz.js'
import {
  findFirstChildByTagSuffix,
  tagMatchesLocalName,
  createWpmlElement
} from './portal-xml-utils.js'

const _ = window._

const fnGetPointIndex = function (point) {
  const idx = parseInt(_.get(point, 'idx'), 10)
  return Number.isFinite(idx) ? idx : 0
}

const fnGetKmlActionsFromProps = function (props) {
  const raw = _.get(props || {}, 'ExtendedData.mis:actions')
  let arr = []
  if (Array.isArray(raw)) {
    arr = raw
  } else if (raw && typeof raw === 'object' && !Object.prototype.hasOwnProperty.call(raw, '_text')) {
    const numericKeys = Object.keys(raw).filter((k) => /^\d+$/.test(String(k)))
    if (numericKeys.length) {
      arr = numericKeys
        .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
        .map((k) => raw[k])
    } else {
      arr = [raw]
    }
  } else {
    arr = (raw === undefined || raw === null) ? [] : [raw]
  }
  const normalized = arr
    .map(normalizeKmlActionEntry)
    .filter((item) => !!item)
  return normalized
}

const fnIsKmlActionPath = function (path) {
  return /^ExtendedData\.mis:actions(\.|$)/.test(String(path || ''))
}

const fnSetKmlActionsInProps = function (props, actions) {
  if (!props || typeof props !== 'object') return
  if (!props.ExtendedData || typeof props.ExtendedData !== 'object') props.ExtendedData = {}
  const normalized = (Array.isArray(actions) ? actions : [])
    .map(normalizeKmlActionEntry)
    .filter((item) => !!item)
  props.ExtendedData['mis:actions'] = normalized.map((item) => {
    if (item.param) return { '@param': item.param, _text: item.text }
    return item.text
  })
}

const fnSetPointKmlActions = function (point, actions, fnGetPointXmlNodes) {
  if (!point) return
  if (!point.props || typeof point.props !== 'object') point.props = {}
  const normalized = (Array.isArray(actions) ? actions : [])
    .map(normalizeKmlActionEntry)
    .filter((item) => !!item)
  fnSetKmlActionsInProps(point.props, normalized)
  if (typeof fnGetPointXmlNodes !== 'function') return

  fnGetPointXmlNodes(point).forEach((node) => {
    if (!node || !node.ownerDocument) return
    let extEl = Array.from(node.children || []).find((ch) => ch.tagName === 'ExtendedData' || /(^|:)ExtendedData$/.test(ch.tagName))
    if (!extEl) {
      extEl = node.ownerDocument.createElement('ExtendedData')
      node.appendChild(extEl)
    }
    if (!extEl.getAttribute('xmlns:mis')) {
      extEl.setAttribute('xmlns:mis', 'www.dji.com')
    }
    Array.from(extEl.children || []).forEach((ch) => {
      if (/(^|:)actions$/.test(ch.tagName || '')) {
        extEl.removeChild(ch)
      }
    })
    normalized.forEach((item) => {
      const actionEl = node.ownerDocument.createElement('mis:actions')
      if (item.param) actionEl.setAttribute('param', item.param)
      actionEl.textContent = item.text
      extEl.appendChild(actionEl)
    })
  })
}

const fnBuildSharedKmlActionsForPoints = function (points) {
  const list = Array.isArray(points) ? points : []
  if (!list.length) return []
  const first = fnGetKmlActionsFromProps(list[0] && list[0].props)
  const firstJson = JSON.stringify(first)
  const same = list.every((pt) => JSON.stringify(fnGetKmlActionsFromProps(pt && pt.props)) === firstJson)
  return same ? first : []
}

const fnGetKmzActionGroupsFromProps = function (props, point) {
  const idx = fnGetPointIndex(point)
  const groupRaw = _.get(props || {}, 'wpml:actionGroup')
  let sourceGroups = []
  if (Array.isArray(groupRaw)) {
    sourceGroups = groupRaw
  } else if (groupRaw && typeof groupRaw === 'object') {
    const numericKeys = Object.keys(groupRaw).filter((k) => /^\d+$/.test(String(k)))
    sourceGroups = numericKeys.length
      ? numericKeys.sort((a, b) => parseInt(a, 10) - parseInt(b, 10)).map((k) => groupRaw[k])
      : [groupRaw]
  }
  if (!sourceGroups.length) return []
  return normalizeKmzActionGroupsForPoint(sourceGroups, idx)
}

const fnGetKmzActionsFromProps = function (props, point) {
  const groups = fnGetKmzActionGroupsFromProps(props, point)
  if (!groups.length) return []
  return (Array.isArray(groups[0].actions) ? groups[0].actions : [])
    .map(normalizeKmzActionEntry)
    .filter((item) => !!item)
}

const fnIsKmzActionPath = function (path) {
  return /^wpml:actionGroup(\.|$)/.test(String(path || ''))
}

const fnSetKmzActionGroupsInProps = function (props, point, groups, options) {
  if (!props || typeof props !== 'object') return []
  const idx = fnGetPointIndex(point)
  const normalizedGroups = normalizeKmzActionGroupsForPoint(groups, idx, options)
  if (!normalizedGroups.length) {
    delete props['wpml:actionGroup']
    return []
  }
  const serializedGroups = normalizedGroups.map((group) => {
    const trigger = {}
    const triggerSrc = (group && group.actionTrigger && typeof group.actionTrigger === 'object') ? group.actionTrigger : {}
    const triggerType = String(triggerSrc.actionTriggerType || KMZ_DEFAULT_ACTION_TRIGGER_TYPE).trim() || KMZ_DEFAULT_ACTION_TRIGGER_TYPE
    trigger['wpml:actionTriggerType'] = triggerType
    Object.keys(triggerSrc).forEach((key) => {
      const safeKey = String(key || '').replace(/^[a-zA-Z_][\w-]*:/, '')
      if (!safeKey || safeKey === 'actionTriggerType') return
      const rawVal = triggerSrc[key]
      if (rawVal === undefined || rawVal === null) return
      trigger['wpml:' + safeKey] = String(rawVal)
    })
    const actions = (Array.isArray(group.actions) ? group.actions : []).map((item, actionIdx) => {
      const normalizedAction = normalizeKmzActionEntry(item)
      if (!normalizedAction) return null
      const safeParams = normalizeKmzActionParamsForActuator(normalizedAction.actuator, normalizedAction.params)
      const actionObj = {
        'wpml:actionId': String(actionIdx),
        'wpml:actionActuatorFunc': normalizedAction.actuator,
        'wpml:actionActuatorFuncParam': {}
      }
      Object.keys(safeParams || {}).forEach((key) => {
        const safeKey = String(key || '').replace(/^[a-zA-Z_][\w-]*:/, '')
        if (!safeKey) return
        actionObj['wpml:actionActuatorFuncParam']['wpml:' + safeKey] = String(safeParams[key])
      })
      return actionObj
    }).filter((item) => !!item)
    return {
      'wpml:actionGroupId': String(group.actionGroupId),
      'wpml:actionGroupStartIndex': String(group.actionGroupStartIndex),
      'wpml:actionGroupEndIndex': String(group.actionGroupEndIndex),
      'wpml:actionGroupMode': String(group.actionGroupMode || KMZ_DEFAULT_ACTION_GROUP_MODE),
      'wpml:actionTrigger': trigger,
      'wpml:action': actions
    }
  })
  props['wpml:actionGroup'] = serializedGroups.length === 1 ? serializedGroups[0] : serializedGroups
  return normalizedGroups
}

const fnSetKmzActionsInProps = function (props, point, actions, options) {
  if (!props || typeof props !== 'object') return []
  const idx = fnGetPointIndex(point)
  const normalizedActions = (Array.isArray(actions) ? actions : [])
    .map(normalizeKmzActionEntry)
    .map((item) => item ? _.assign({}, item, { params: normalizeKmzActionParamsForActuator(item.actuator, item.params) }) : item)
    .filter((item) => !!item)
  const existingGroups = fnGetKmzActionGroupsFromProps(props, point)
  const baseGroup = existingGroups.length
    ? _.cloneDeep(existingGroups[0])
    : normalizeKmzActionGroupEntry({
        'wpml:actionGroupId': idx,
        'wpml:actionGroupStartIndex': idx,
        'wpml:actionGroupEndIndex': idx,
        'wpml:actionGroupMode': KMZ_DEFAULT_ACTION_GROUP_MODE,
        'wpml:actionTrigger': { 'wpml:actionTriggerType': KMZ_DEFAULT_ACTION_TRIGGER_TYPE }
      }, idx)
  baseGroup.actions = normalizedActions
  const nextGroups = [baseGroup].concat(existingGroups.slice(1))
  return fnSetKmzActionGroupsInProps(props, point, nextGroups, options)
}

const fnCreateWpmlElement = function (doc, tagLocalName, WPML_NAMESPACE_URI) {
  return createWpmlElement(doc, tagLocalName, WPML_NAMESPACE_URI)
}

const fnSetPointKmzActionGroups = function (point, groups, options, fnGetPointXmlNodes, fnScheduleKmzUpdateTimeTouch, WPML_NAMESPACE_URI) {
  if (!point) return []
  if (!point.props || typeof point.props !== 'object') point.props = {}
  const normalizedGroups = fnSetKmzActionGroupsInProps(point.props, point, groups, options)
  if (typeof fnGetPointXmlNodes !== 'function') return normalizedGroups

  const xmlNodes = (Array.isArray(point.linkedXmlEls) && point.linkedXmlEls.length)
    ? point.linkedXmlEls.filter(Boolean)
    : fnGetPointXmlNodes(point)
  xmlNodes.forEach((node) => {
    if (!node || !node.ownerDocument) return
    const existing = Array.from(node.children || []).filter((ch) => tagMatchesLocalName(ch.tagName, 'actionGroup'))
    existing.forEach((groupEl) => {
      if (groupEl.parentNode === node) node.removeChild(groupEl)
    })
    const anchorEl = findFirstChildByTagSuffix(node, 'waypointGimbalHeadingParam')
      || findFirstChildByTagSuffix(node, 'isRisky')
      || null
    const fnAppendWpmlTextChild = function (parent, tagName, value) {
      const el = fnCreateWpmlElement(node.ownerDocument, tagName, WPML_NAMESPACE_URI)
      if (!el) return null
      el.textContent = String(value === undefined || value === null ? '' : value)
      parent.appendChild(el)
      return el
    }
    normalizedGroups.forEach((group) => {
      const groupEl = fnCreateWpmlElement(node.ownerDocument, 'actionGroup', WPML_NAMESPACE_URI)
      if (!groupEl) return
      fnAppendWpmlTextChild(groupEl, 'actionGroupId', group.actionGroupId)
      fnAppendWpmlTextChild(groupEl, 'actionGroupStartIndex', group.actionGroupStartIndex)
      fnAppendWpmlTextChild(groupEl, 'actionGroupEndIndex', group.actionGroupEndIndex)
      fnAppendWpmlTextChild(groupEl, 'actionGroupMode', group.actionGroupMode || KMZ_DEFAULT_ACTION_GROUP_MODE)

      const triggerEl = fnCreateWpmlElement(node.ownerDocument, 'actionTrigger', WPML_NAMESPACE_URI)
      if (triggerEl) {
        const trigger = (group && group.actionTrigger && typeof group.actionTrigger === 'object') ? group.actionTrigger : {}
        fnAppendWpmlTextChild(triggerEl, 'actionTriggerType', String(trigger.actionTriggerType || KMZ_DEFAULT_ACTION_TRIGGER_TYPE))
        Object.keys(trigger).forEach((key) => {
          const safeKey = String(key || '').replace(/^[a-zA-Z_][\w-]*:/, '')
          if (!safeKey || safeKey === 'actionTriggerType') return
          fnAppendWpmlTextChild(triggerEl, safeKey, trigger[key])
        })
        groupEl.appendChild(triggerEl)
      }

      const actions = Array.isArray(group && group.actions) ? group.actions : []
      actions.forEach((item, actionIdx) => {
        const normalizedAction = normalizeKmzActionEntry(item)
        if (!normalizedAction) return
        const actionEl = fnCreateWpmlElement(node.ownerDocument, 'action', WPML_NAMESPACE_URI)
        if (!actionEl) return
        fnAppendWpmlTextChild(actionEl, 'actionId', actionIdx)
        fnAppendWpmlTextChild(actionEl, 'actionActuatorFunc', normalizedAction.actuator)
        const paramsEl = fnCreateWpmlElement(node.ownerDocument, 'actionActuatorFuncParam', WPML_NAMESPACE_URI)
        if (paramsEl) {
          const safeParams = normalizeKmzActionParamsForActuator(normalizedAction.actuator, normalizedAction.params)
          Object.keys(safeParams || {}).forEach((key) => {
            const safeKey = String(key || '').replace(/^[a-zA-Z_][\w-]*:/, '')
            if (!safeKey) return
            const pEl = fnCreateWpmlElement(node.ownerDocument, safeKey, WPML_NAMESPACE_URI)
            if (!pEl) return
            pEl.textContent = String(safeParams[key] || '')
            paramsEl.appendChild(pEl)
          })
          actionEl.appendChild(paramsEl)
        }
        groupEl.appendChild(actionEl)
      })

      if (anchorEl && anchorEl.parentNode === node) {
        node.insertBefore(groupEl, anchorEl)
      } else {
        node.appendChild(groupEl)
      }
    })
  })
  if (typeof fnScheduleKmzUpdateTimeTouch === 'function') {
    fnScheduleKmzUpdateTimeTouch('wpml:actionGroup')
  }
  return normalizedGroups
}

const fnSetPointKmzActions = function (point, actions, options, fnGetPointName, fnGetPointXmlNodes, fnScheduleKmzUpdateTimeTouch, WPML_NAMESPACE_URI) {
  if (!point) return []
  if (!point.props || typeof point.props !== 'object') point.props = {}
  let normalized = (Array.isArray(actions) ? actions : [])
    .map(normalizeKmzActionEntry)
    .map((item) => item ? _.assign({}, item, { params: normalizeKmzActionParamsForActuator(item.actuator, item.params) }) : item)
    .filter((item) => !!item)
  normalized = resolveKmzActionFileSuffixPointNameTokens(normalized, function () {
    return (typeof fnGetPointName === 'function') ? fnGetPointName(point) : ''
  })
  const existingGroups = fnGetKmzActionGroupsFromProps(point.props, point)
  const idx = fnGetPointIndex(point)
  const baseGroup = existingGroups.length
    ? _.cloneDeep(existingGroups[0])
    : normalizeKmzActionGroupEntry({
        'wpml:actionGroupId': idx,
        'wpml:actionGroupStartIndex': idx,
        'wpml:actionGroupEndIndex': idx,
        'wpml:actionGroupMode': KMZ_DEFAULT_ACTION_GROUP_MODE,
        'wpml:actionTrigger': { 'wpml:actionTriggerType': KMZ_DEFAULT_ACTION_TRIGGER_TYPE }
      }, idx)
  baseGroup.actions = normalized
  const nextGroups = [baseGroup].concat(existingGroups.slice(1))
  return fnSetPointKmzActionGroups(point, nextGroups, options, fnGetPointXmlNodes, fnScheduleKmzUpdateTimeTouch, WPML_NAMESPACE_URI)
}

const fnApplyKmzActionFileSuffixDisplayModeToGroups = function (groups, mode, pointNameResolver) {
  const normalizedGroups = normalizeKmzActionGroupsForPoint(groups, 0)
  return normalizedGroups.map((group) => {
    return _.assign({}, group, {
      actions: applyKmzActionFileSuffixDisplayMode(group.actions, mode, pointNameResolver)
    })
  })
}

const fnResolveKmzActionFileSuffixPointNameTokensInGroups = function (groups, pointNameResolver) {
  const normalizedGroups = normalizeKmzActionGroupsForPoint(groups, 0)
  return normalizedGroups.map((group) => {
    return _.assign({}, group, {
      actions: resolveKmzActionFileSuffixPointNameTokens(group.actions, pointNameResolver)
    })
  })
}

const fnSyncPointActionFileSuffixWithName = function (point, explicitName, fnGetPointName, fnSetPointKmzActionGroups) {
  if (!point || !point.props || typeof point.props !== 'object') return false
  const pointName = String(explicitName === undefined
    ? ((typeof fnGetPointName === 'function') ? fnGetPointName(point) : '')
    : explicitName)
  const groups = fnGetKmzActionGroupsFromProps(point.props, point)
  if (!groups.length) return false
  let changed = false
  const updatedGroups = groups.map((group) => {
    const actions = Array.isArray(group.actions) ? group.actions : []
    const nextActions = actions.map((item) => {
      if (!item || !item.params || typeof item.params !== 'object') return item
      if (!Object.prototype.hasOwnProperty.call(item.params, 'fileSuffix')) return item
      const current = String(item.params.fileSuffix === undefined ? '' : item.params.fileSuffix)
      if (current === pointName) return item
      changed = true
      return _.assign({}, item, {
        params: _.assign({}, item.params, { fileSuffix: pointName })
      })
    })
    return _.assign({}, group, { actions: nextActions })
  })
  if (!changed) return false
  if (typeof fnSetPointKmzActionGroups === 'function') {
    fnSetPointKmzActionGroups(point, updatedGroups)
  }
  return true
}

const fnBuildKmzActionGroupComparableShape = function (group) {
  const normalizedGroup = normalizeKmzActionGroupEntry(group, 0)
  if (!normalizedGroup) return null

  const triggerSrc = normalizeKmzActionTriggerEntry(normalizedGroup.actionTrigger)
  const triggerType = String(triggerSrc.actionTriggerType || KMZ_DEFAULT_ACTION_TRIGGER_TYPE).trim() || KMZ_DEFAULT_ACTION_TRIGGER_TYPE
  const triggerParams = {}
  Object.keys(triggerSrc || {})
    .filter((key) => String(key || '').trim() && key !== 'actionTriggerType')
    .sort((a, b) => String(a).localeCompare(String(b)))
    .forEach((key) => {
      const safeKey = String(key || '').trim()
      if (!safeKey) return
      const rawVal = triggerSrc[key]
      triggerParams[safeKey] = String(rawVal === undefined || rawVal === null ? '' : rawVal)
    })

  const actions = (Array.isArray(normalizedGroup.actions) ? normalizedGroup.actions : [])
    .map(normalizeKmzActionEntry)
    .filter((item) => !!item)
    .map((item) => {
      const actuator = String(item.actuator || '').trim()
      const paramsSrc = normalizeKmzActionParamsForActuator(actuator, item.params)
      const params = {}
      Object.keys(paramsSrc || {})
        .sort((a, b) => String(a).localeCompare(String(b)))
        .forEach((key) => {
          const safeKey = String(key || '').trim()
          if (!safeKey) return
          const rawVal = paramsSrc[key]
          params[safeKey] = String(rawVal === undefined || rawVal === null ? '' : rawVal)
        })
      return {
        actuator: actuator,
        params: params
      }
    })

  return {
    actionGroupMode: String(normalizedGroup.actionGroupMode || KMZ_DEFAULT_ACTION_GROUP_MODE).trim() || KMZ_DEFAULT_ACTION_GROUP_MODE,
    actionTriggerType: triggerType,
    actionTriggerParams: triggerParams,
    actions: actions
  }
}

const fnBuildSharedKmzActionGroupsForPoints = function (points) {
  const list = Array.isArray(points) ? points : []
  if (!list.length) return []
  const firstGroups = fnApplyKmzActionFileSuffixDisplayModeToGroups(fnGetKmzActionGroupsFromProps(list[0] && list[0].props, list[0]), 'token')
  const firstComparable = firstGroups
    .map(fnBuildKmzActionGroupComparableShape)
    .filter((item) => !!item)
  const firstJson = JSON.stringify(firstComparable)
  const same = list.every((pt) => {
    const next = fnApplyKmzActionFileSuffixDisplayModeToGroups(fnGetKmzActionGroupsFromProps(pt && pt.props, pt), 'token')
    const nextComparable = next
      .map(fnBuildKmzActionGroupComparableShape)
      .filter((item) => !!item)
    return JSON.stringify(nextComparable) === firstJson
  })
  return same ? firstGroups : []
}

const fnBuildSharedKmzActionsForPoints = function (points) {
  const groups = fnBuildSharedKmzActionGroupsForPoints(points)
  if (!groups.length) return []
  return Array.isArray(groups[0].actions) ? groups[0].actions : []
}

const fnToBoolLike = function (value) {
  const raw = String(value === null || value === undefined ? '' : value).trim().toLowerCase()
  if (!raw) return null
  if (raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on') return true
  if (raw === '0' || raw === 'false' || raw === 'no' || raw === 'off') return false
  const n = Number(raw)
  if (Number.isFinite(n)) return n > 0
  return null
}

export {
  fnGetKmlActionsFromProps,
  fnIsKmlActionPath,
  fnSetKmlActionsInProps,
  fnSetPointKmlActions,
  fnBuildSharedKmlActionsForPoints,
  fnGetKmzActionGroupsFromProps,
  fnGetKmzActionsFromProps,
  fnIsKmzActionPath,
  fnSetKmzActionGroupsInProps,
  fnSetKmzActionsInProps,
  fnSetPointKmzActionGroups,
  fnSetPointKmzActions,
  fnApplyKmzActionFileSuffixDisplayModeToGroups,
  fnResolveKmzActionFileSuffixPointNameTokensInGroups,
  fnSyncPointActionFileSuffixWithName,
  fnBuildKmzActionGroupComparableShape,
  fnBuildSharedKmzActionGroupsForPoints,
  fnBuildSharedKmzActionsForPoints,
  fnToBoolLike
}
